import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { parseShippingMethods, addBusinessDays } from '../../util/dates';
import { hideTidioChat, openTidioChat, showTidioChat } from '../../util/tidioChat';
import deliveryIcon from '../../assets/delivery-icon.png';
import css from './ListingPage.module.css';

const parseWarrantyContent = raw => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(item => String(item || '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return parseWarrantyContent(parsed);
    } catch (e) {
      return trimmed
        .split(/\r?\n/)
        .map(item => item.trim())
        .filter(Boolean);
    }
  }
  return [String(raw).trim()].filter(Boolean);
};

const DeliveryConditionsPanel = ({ isOpen, onClose, listing, price, intl }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;

    hideTidioChat();
    document.body.style.overflow = 'hidden';
    const handleEscape = e => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      showTidioChat();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const shippingMethodsRaw = listing?.attributes?.publicData?.shippingMethods;
  const warrantyItems = parseWarrantyContent(listing?.attributes?.publicData?.warranty);
  const shippingMethods = parseShippingMethods(shippingMethodsRaw);
  const listingPriceAmount = price?.amount || 0;
  const currency = price?.currency || 'EUR';

  const isMethodFree = method => {
    if (!method) return false;
    const franco = method.franco || 0;
    return listingPriceAmount >= franco && franco > 0;
  };

  const formatPrice = method => {
    if (isMethodFree(method)) return 'Gratuit';
    const shippingPrice = method.priceOneItem || 0;
    return intl.formatNumber(shippingPrice / 100, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatFranco = francoAmount => {
    return intl.formatNumber(francoAmount / 100, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getDeliveryDate = delayDays => {
    if (!delayDays || delayDays <= 0) return null;
    const d = addBusinessDays(new Date(), delayDays);
    return intl.formatDate(d, { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const methodLabel = method => {
    if (method.id === 'relay') return 'Point relais';
    return 'Domicile';
  };

  const handleOpenChat = () => {
    onClose();
    // Après fermeture du panneau, le cleanup réaffiche Tidio ; ouvrir le chat ensuite.
    setTimeout(() => openTidioChat(), 0);
  };

  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <>
      <div className={css.dcpOverlay} onClick={onClose} />
      <div className={css.dcpPanel}>
        <div className={css.dcpHeader}>
          <h2 className={css.dcpTitle}>Livraison, retours & garantie</h2>
          <button className={css.dcpClose} onClick={onClose} aria-label="Fermer">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className={css.dcpContent}>
          {/* --- Livraison --- */}
          <section className={css.dcpSection}>
            <h3 className={css.dcpSectionTitle}>
              <img src={deliveryIcon} alt="" className={css.dcpSectionIcon} aria-hidden="true" />
              Livraison
            </h3>
            <p className={css.dcpDeliveryHint}>
              La methode de livraison est a choisir au moment de la commande.
            </p>
            {shippingMethods && shippingMethods.length > 0 ? (
              <div className={css.dcpMethodsList}>
                {shippingMethods.map(method => {
                  const delay = parseInt(method.delay, 10);
                  const date = getDeliveryDate(delay);
                  const priceLabel = formatPrice(method);
                  const carrier = method.carrier || '';
                  const isFree = isMethodFree(method);
                  const franco = method.franco || 0;
                  const showFrancoNotice = franco > 0 && franco <= 200000 && listingPriceAmount < franco;

                  return (
                    <div key={method.id} className={css.dcpMethodCard}>
                      <div className={css.dcpMethodHeader}>
                        <span className={css.dcpMethodName}>{methodLabel(method)}</span>
                        <span className={isFree ? css.dcpMethodPriceFree : css.dcpMethodPrice}>
                          {priceLabel}
                        </span>
                      </div>
                      {carrier && (
                        <p className={css.dcpMethodDetail}>Transporteur : {carrier}</p>
                      )}
                      {date && (
                        <p className={css.dcpMethodDetail}>Livraison estimée le {date}</p>
                      )}
                      {delay > 0 && (
                        <p className={css.dcpMethodDetail}>
                          Délai : {delay} jour{delay > 1 ? 's' : ''} ouvré{delay > 1 ? 's' : ''}
                        </p>
                      )}
                      {showFrancoNotice && (
                        <p className={css.dcpMethodFrancoNotice}>
                          Frais de livraison offerts a partir de {formatFranco(franco)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className={css.dcpPlaceholder}>
                Les informations de livraison ne sont pas disponibles pour ce produit.
              </p>
            )}
          </section>

          {/* --- Retours --- */}
          <section className={css.dcpSection}>
            <h3 className={css.dcpSectionTitle}>
              <svg className={css.dcpSectionIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 8L3.64645 8.35355L3.29289 8L3.64645 7.64645L4 8ZM9 19.5C8.72386 19.5 8.5 19.2761 8.5 19C8.5 18.7239 8.72386 18.5 9 18.5L9 19.5ZM8.64645 13.3536L3.64645 8.35355L4.35355 7.64645L9.35355 12.6464L8.64645 13.3536ZM3.64645 7.64645L8.64645 2.64645L9.35355 3.35355L4.35355 8.35355L3.64645 7.64645ZM4 7.5L14.5 7.5L14.5 8.5L4 8.5L4 7.5ZM14.5 19.5L9 19.5L9 18.5L14.5 18.5L14.5 19.5ZM20.5 13.5C20.5 16.8137 17.8137 19.5 14.5 19.5L14.5 18.5C17.2614 18.5 19.5 16.2614 19.5 13.5L20.5 13.5ZM14.5 7.5C17.8137 7.5 20.5 10.1863 20.5 13.5L19.5 13.5C19.5 10.7386 17.2614 8.5 14.5 8.5L14.5 7.5Z" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
              Retours
            </h3>
            <div className={css.dcpInfoCard}>
              <p className={css.dcpInfoHighlight}>14 jours pour changer d'avis</p>
              <p className={css.dcpInfoText}>
                Vous disposez de 14 jours après réception de votre commande pour nous retourner
                un produit conformément au délai légal de retractation.
              </p>
            </div>
          </section>

          {/* --- Garantie --- */}
          <section className={css.dcpSection}>
            <h3 className={css.dcpSectionTitle}>
              <svg className={css.dcpSectionIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Garantie
            </h3>
            <div className={css.dcpInfoCard}>
              <p className={css.dcpInfoHighlight}>Garantie produit</p>
              {warrantyItems.length > 0 ? (
                warrantyItems.map((item, index) => (
                  <p key={index} className={css.dcpInfoText}>
                    {item}
                  </p>
                ))
              ) : (
                <p className={css.dcpInfoText}>
                  Tous les produits vendus sur Neokasa bénéficient de la garantie légale de
                  conformité de 2 ans. En cas de défaut constaté, contactez notre service client
                  pour organiser un retour ou un échange.
                </p>
              )}
            </div>
          </section>

          {/* --- Service client --- */}
          <section className={css.dcpSection}>
            <h3 className={css.dcpSectionTitle}>
              <svg className={css.dcpSectionIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <rect fill="none" stroke="currentColor" strokeWidth="1.5" x="6.27" y="5.32" width="11.45" height="15.27" rx="5.73"/>
                <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M17.73,9.14h1.91A2.86,2.86,0,0,1,22.5,12v1.91a2.86,2.86,0,0,1-2.86,2.86H17.73"/>
                <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M6.27,16.77H4.36A2.86,2.86,0,0,1,1.5,13.91V12A2.86,2.86,0,0,1,4.36,9.14H6.27"/>
                <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M4.36,9.14A7.64,7.64,0,0,1,12,1.5h0a7.64,7.64,0,0,1,7.64,7.64"/>
                <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M19.64,16.77v1a4.78,4.78,0,0,1-4.78,4.77"/>
              </svg>
              Service client
            </h3>
            <div className={css.dcpServiceGrid}>
              <div className={css.dcpServiceCard}>
                <div className={css.dcpServiceHeader}>
                  <svg className={css.dcpServiceIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className={css.dcpServiceLabel}>Téléphone</span>
                </div>
                <a href="tel:+33189480612" className={css.dcpServiceValue}>01&nbsp;89&nbsp;48&nbsp;06&nbsp;12</a>
                <p className={css.dcpServiceHours}>Lun-Ven, 09h-18h (hors jours fériés)</p>
                <p className={css.dcpServiceNote}>Appel gratuit</p>
              </div>

              <div className={css.dcpServiceCard}>
                <div className={css.dcpServiceHeader}>
                  <svg className={css.dcpServiceIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className={css.dcpServiceLabel}>Email</span>
                </div>
                <a href="mailto:contact@neokasa.fr" className={css.dcpServiceValue}>contact@neokasa.fr</a>
                <p className={css.dcpServiceHours}>Réponse sous 24h ouvrées</p>
              </div>

              <div className={css.dcpServiceCard}>
                <div className={css.dcpServiceHeader}>
                  <svg className={css.dcpServiceIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className={css.dcpServiceLabel}>Chat en direct</span>
                </div>
                <p className={css.dcpServiceHours}>Lun-Ven, 09h-18h (hors jours fériés)</p>
                <button type="button" className={css.dcpChatButton} onClick={handleOpenChat}>
                  Ouvrir le chat
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>,
    document.body
  );
};

export default DeliveryConditionsPanel;
